/**
 * FestivalEditorDialog — admin form for creating/editing any event.
 *
 * One dialog handles every event category (festival, concert, party,
 * sports, market, civic, private_event) since they all share the
 * same underlying schema. The category dropdown drives a few visual
 * cues (default theme color, default sponsorship tier preset) but
 * not the structural shape of the form.
 *
 * Why one dialog for create AND edit?
 * ───────────────────────────────────
 * The slug is the natural key. The admin_upsert_festival RPC handles
 * both paths transparently — pass an existing slug to update, a new
 * one to insert. Splitting create/edit into two dialogs would
 * duplicate every field validator and state hook for zero benefit.
 *
 * Sections (top to bottom)
 * ────────────────────────
 *   1. Identity     — name, slug, category, dates, description
 *   2. Tickets      — Phase 1 link-out fields (buy link, partner, price)
 *   3. Branding     — theme color, emoji, hero image URL
 *   4. Venue        — name + lat/lng (typed manually for v1; future:
 *                     map picker like the campaign wizard's preview)
 *   5. Reach        — corridor multi-select (from corridors table)
 *   6. Sub-events   — repeater for {slug, name, dates}
 *   7. Sponsorship  — repeater for tier defaults
 *      tiers
 *   8. Recurrence   — annual flag + note for ops
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { deleteSponsorCreativeByUrl } from '@/lib/storageCleanup';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Trash2, Plus, Loader2, Music, Mic, PartyPopper, Trophy,
  ShoppingBasket, Flag, Sparkles, Ticket,
} from 'lucide-react';
import { toast } from 'sonner';
import type {
  FestivalEntry, FestivalSubEvent, FestivalTierConfig, FestivalTierKey,
} from '@/stores/campaignWizardStore';
import { FestivalVenuePinPicker } from '@/components/FestivalVenuePinPicker';
import { ImageUploadInput } from '@/components/ImageUploadInput';
import { VideoUploadInput } from '@/components/VideoUploadInput';

// ── Types ─────────────────────────────────────────────────────────────────

type Category =
  | 'festival' | 'concert' | 'party' | 'sports' | 'market' | 'civic' | 'private_event';

interface CategoryDef {
  key: Category;
  label: string;
  description: string;
  icon: any;
  defaultThemeColor: string;
  defaultEmoji: string;
}

const CATEGORIES: CategoryDef[] = [
  { key: 'festival',      label: 'Festival',         description: 'Multi-day cultural event (WCMF, Carnival, Independence)', icon: Music,         defaultThemeColor: '#7c3aed', defaultEmoji: '🎶' },
  { key: 'concert',       label: 'Concert',          description: 'Single-night music or performance event',                  icon: Mic,           defaultThemeColor: '#ec4899', defaultEmoji: '🎤' },
  { key: 'party',         label: 'Party',            description: 'Nightclub, beach, or rooftop party',                       icon: PartyPopper,   defaultThemeColor: '#f59e0b', defaultEmoji: '🎉' },
  { key: 'sports',        label: 'Sports event',     description: 'Match, regatta, race, fitness challenge',                  icon: Trophy,        defaultThemeColor: '#15803d', defaultEmoji: '🏆' },
  { key: 'market',        label: 'Market / fair',    description: 'Food festival, craft market, heritage village',            icon: ShoppingBasket,defaultThemeColor: '#06b6d4', defaultEmoji: '🛍️' },
  { key: 'civic',         label: 'Civic event',      description: 'National day, community parade, charity event',            icon: Flag,          defaultThemeColor: '#16a34a', defaultEmoji: '🇩🇲' },
  { key: 'private_event', label: 'Private event',    description: 'Wedding, corporate gala, brand activation',                icon: Sparkles,      defaultThemeColor: '#a855f7', defaultEmoji: '✨' },
];

// Default tier preset per category — gives the operator a sensible
// starting point. Pricing curves match the seed migration's defaults
// so cross-category comparisons stay coherent.
const DEFAULT_TIER_PRESETS: Record<Category, FestivalTierConfig[]> = {
  festival: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 8000, rate_xcd_max: 15000, perks: ['Whole-event branding', 'Full-screen splash when the app opens', 'Logo on every surface'] },
    { tier: 'stage',         label: 'Stage sponsor',       slot_count: 3, rate_xcd_min: 2500, rate_xcd_max: 5000,  perks: ['Sub-event takeover'] },
    { tier: 'corridor',      label: 'Corridor sponsor',    slot_count: 2, rate_xcd_min: 1500, rate_xcd_max: 3000,  perks: ['Festival-day banner'] },
    { tier: 'venue_partner', label: 'Venue partner',       slot_count: 6, rate_xcd_min: 800,  rate_xcd_max: 1500,  perks: ['Featured map pin'] },
    { tier: 'after_party',   label: 'After-party sponsor', slot_count: 1, rate_xcd_min: 1200, rate_xcd_max: 2000,  perks: ['Late-night ride takeover'] },
  ],
  concert: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 3000, rate_xcd_max: 6000,  perks: ['"Presented by" branding', 'Day-of splash when the app opens'] },
    { tier: 'venue_partner', label: 'Venue partner',       slot_count: 3, rate_xcd_min: 600,  rate_xcd_max: 1200,  perks: ['Featured map pin'] },
    { tier: 'after_party',   label: 'After-party sponsor', slot_count: 1, rate_xcd_min: 800,  rate_xcd_max: 1500,  perks: ['Late-night ride takeover'] },
  ],
  party: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 800,  rate_xcd_max: 2000,  perks: ['"Presented by" branding'] },
    { tier: 'venue_partner', label: 'Venue partner',       slot_count: 2, rate_xcd_min: 300,  rate_xcd_max: 700,   perks: ['Featured map pin'] },
  ],
  sports: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 2000, rate_xcd_max: 5000,  perks: ['Event branding'] },
    { tier: 'venue_partner', label: 'Venue partner',       slot_count: 4, rate_xcd_min: 500,  rate_xcd_max: 1000,  perks: ['Featured map pin near venue'] },
  ],
  market: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 1000, rate_xcd_max: 3000,  perks: ['Event branding'] },
    { tier: 'venue_partner', label: 'Vendor highlight',    slot_count: 8, rate_xcd_min: 200,  rate_xcd_max: 500,   perks: ['Featured map pin during event'] },
  ],
  civic: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 1500, rate_xcd_max: 4000,  perks: ['Event branding'] },
    { tier: 'corridor',      label: 'Corridor sponsor',    slot_count: 2, rate_xcd_min: 800,  rate_xcd_max: 1800,  perks: ['Event-day banner'] },
  ],
  private_event: [
    { tier: 'title',         label: 'Title sponsor',       slot_count: 1, rate_xcd_min: 5000, rate_xcd_max: 12000, perks: ['Bespoke branding package'] },
  ],
};

// Slug helper — title-cases input safely and falls back to a
// timestamped suffix if the input is empty.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// Dominica is on Atlantic Standard Time (AST) year-round — UTC-4, no DST.
const DOMINICA_UTC_OFFSET_MS = 4 * 60 * 60 * 1000;
const DOMINICA_OFFSET_SUFFIX = '-04:00';

/**
 * Stored instant (UTC ISO) → the `datetime-local` value to show in the input,
 * expressed in DOMINICA wall-clock time. Festivals must light up at the
 * event's real local time, so operators always edit in Dominica time even
 * when their own computer is in another timezone.
 */
function isoDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  // Shift the instant into AST, then read the UTC fields to get the
  // Dominica wall-clock "YYYY-MM-DDTHH:mm".
  const ast = new Date(d.getTime() - DOMINICA_UTC_OFFSET_MS);
  return ast.toISOString().slice(0, 16);
}

/**
 * `datetime-local` value (a Dominica wall-clock with no zone) → UTC ISO
 * instant. We tag the wall-clock as AST so "6:00 AM" always means 6 AM in
 * Dominica, regardless of the operator's browser timezone.
 */
function dominicaLocalToIso(local: string | null | undefined): string | null {
  if (!local) return null;
  const wall = local.slice(0, 16); // YYYY-MM-DDTHH:mm
  const d = new Date(`${wall}:00${DOMINICA_OFFSET_SUFFIX}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Normalise an operator-typed link so the mobile app can actually open
 * it. Bare hosts ("www.google.com") get an https:// prefix; anything
 * with an explicit scheme is left untouched; blank → null.
 */
function normalizeUrlMaybe(input: string): string | null {
  const t = (input || '').trim();
  if (!t) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t;
  return `https://${t}`;
}

// ── Component ─────────────────────────────────────────────────────────────

interface FestivalEditorDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Pass the existing festival to edit. Pass null/undefined to create. */
  festival?: FestivalEntry | null;
  onSaved: () => void;
}

export default function FestivalEditorDialog({
  open, onOpenChange, festival, onSaved,
}: FestivalEditorDialogProps) {
  const isEdit = !!festival;

  // ── Form state ─────────────────────────────────────────────────────────

  const [category, setCategory] = useState<Category>('festival');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [organizer, setOrganizer] = useState('');
  const [externalUrl, setExternalUrl] = useState('');

  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  // Optional "start promoting on" instant. Blank = only show during the
  // live window (legacy). Set earlier than startsAt to hype the event in
  // the app ahead of time. Persisted via the direct row write below (not
  // an admin_upsert_festival param), same pattern as promo_video_url.
  const [promoStartsAt, setPromoStartsAt] = useState('');
  const originalPromoStartsAtRef = useRef('');

  const [themeColor, setThemeColor] = useState('#7c3aed');
  const [emoji, setEmoji] = useState('🎶');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  // The hero image URL persisted on the row when the dialog opened. Used
  // to reclaim the old file from storage after a save that replaced or
  // cleared it, so swapping a flier doesn't orphan bytes in the bucket.
  const originalHeroImageUrlRef = useRef('');
  // Opt-in promo video shown as a tap-to-play card on the mobile
  // festival detail sheet. Persisted directly to the row after the
  // upsert RPC returns an id (see handleSave).
  const [promoVideoUrl, setPromoVideoUrl] = useState('');
  // The promo video URL currently persisted on the row (from the direct
  // read on open). Used to delete the old file from storage after a save
  // that changed or cleared it, so replacements don't orphan.
  const originalPromoVideoUrlRef = useRef('');

  const [venueName, setVenueName] = useState('');
  const [venueLat, setVenueLat] = useState('');
  const [venueLng, setVenueLng] = useState('');

  const [corridorIds, setCorridorIds] = useState<string[]>([]);
  const [allCorridors, setAllCorridors] = useState<{ id: string; name: string }[]>([]);

  // Audience targeting. Mirrors `ad_campaigns.audience` semantics so
  // operators learn one concept for both campaign banners and event
  // glows: 'all' = both apps, 'passenger' = passenger app only,
  // 'driver' = driver app (Discover tab) only, 'tourist' = passenger
  // / tourist-tagged users only. Default 'all' keeps the legacy
  // behaviour where every festival is visible to every app.
  const [audience, setAudience] = useState<'all' | 'passenger' | 'driver' | 'tourist'>('all');

  const [subEvents, setSubEvents] = useState<FestivalSubEvent[]>([]);
  const [tiers, setTiers] = useState<FestivalTierConfig[]>([]);

  const [ticketUrl, setTicketUrl] = useState('');
  const [ticketPartnerName, setTicketPartnerName] = useState('');
  const [ticketPriceFromXcd, setTicketPriceFromXcd] = useState('');
  const [ticketsOnSaleAt, setTicketsOnSaleAt] = useState('');
  // Optional online sales cutoff. Persisted via the direct row write
  // (not an admin_upsert_festival param), same pattern as promo_starts_at.
  const [ticketsCloseAt, setTicketsCloseAt] = useState('');

  const [isRecurring, setIsRecurring] = useState(true);
  const [recurrenceNote, setRecurrenceNote] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [saving, setSaving] = useState(false);

  // ── Lifecycle: when the dialog opens, hydrate the form ──────────────────

  useEffect(() => {
    if (!open) return;
    if (festival) {
      setCategory(((festival as any).category as Category) || 'festival');
      setName(festival.name || '');
      setSlug(festival.slug || '');
      setSlugTouched(true);
      setDescription(festival.description || '');
      setOrganizer(festival.organizer_name || '');
      setExternalUrl(festival.external_url || '');
      setStartsAt(isoDate(festival.starts_at));
      setEndsAt(isoDate(festival.ends_at));
      // Seed from the entry (may be absent on the list RPC) and top up via
      // the direct read below.
      setPromoStartsAt(isoDate((festival as any).promo_starts_at));
      originalPromoStartsAtRef.current = isoDate((festival as any).promo_starts_at);
      setThemeColor(festival.theme_color || '#7c3aed');
      setEmoji(festival.emoji || '🎶');
      setHeroImageUrl(festival.hero_image_url || '');
      originalHeroImageUrlRef.current = festival.hero_image_url || '';
      // admin_list_festivals doesn't (yet) surface promo_video_url, so
      // seed from whatever the entry carries and top up via a direct
      // read below.
      setPromoVideoUrl((festival as any).promo_video_url || '');
      originalPromoVideoUrlRef.current = (festival as any).promo_video_url || '';
      setVenueName(festival.venue_name || '');
      setVenueLat(festival.venue_lat != null ? String(festival.venue_lat) : '');
      setVenueLng(festival.venue_lng != null ? String(festival.venue_lng) : '');
      setCorridorIds(festival.corridor_ids || []);
      // The admin_list_festivals RPC returns `audience` as a string.
      // Legacy rows before this migration default to 'all' server-
      // side, so a missing value here is safe to treat as 'all'.
      {
        const rawAudience = (festival as any).audience;
        setAudience(
          rawAudience === 'passenger' || rawAudience === 'driver' || rawAudience === 'tourist'
            ? rawAudience
            : 'all',
        );
      }
      setSubEvents(festival.sub_events || []);
      setTiers(festival.sponsorship_tiers || []);
      setTicketUrl((festival as any).ticket_url || '');
      setTicketPartnerName((festival as any).ticket_partner_name || '');
      setTicketPriceFromXcd(
        (festival as any).ticket_price_from_xcd != null
          ? String((festival as any).ticket_price_from_xcd)
          : '',
      );
      setTicketsOnSaleAt(isoDate((festival as any).tickets_on_sale_at));
      setTicketsCloseAt(isoDate((festival as any).tickets_close_at));
      setIsRecurring(festival.is_recurring ?? true);
      setRecurrenceNote((festival as any).recurrence_note || '');
      setIsActive(festival.is_active ?? true);
    } else {
      // Reset for create
      setCategory('festival');
      setName(''); setSlug(''); setSlugTouched(false);
      setDescription(''); setOrganizer(''); setExternalUrl('');
      setStartsAt(''); setEndsAt('');
      setPromoStartsAt('');
      originalPromoStartsAtRef.current = '';
      const def = CATEGORIES.find((c) => c.key === 'festival')!;
      setThemeColor(def.defaultThemeColor);
      setEmoji(def.defaultEmoji);
      setHeroImageUrl('');
      originalHeroImageUrlRef.current = '';
      setPromoVideoUrl('');
      originalPromoVideoUrlRef.current = '';
      setVenueName(''); setVenueLat(''); setVenueLng('');
      setCorridorIds([]);
      setAudience('all');
      setSubEvents([]);
      setTiers(DEFAULT_TIER_PRESETS.festival);
      setTicketUrl(''); setTicketPartnerName('');
      setTicketPriceFromXcd(''); setTicketsOnSaleAt(''); setTicketsCloseAt('');
      setIsRecurring(true); setRecurrenceNote('');
      setIsActive(true);
    }
  }, [open, festival]);

  // Load corridors once when the dialog opens
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('corridors')
        .select('id, name')
        .order('name');
      if (alive && data) setAllCorridors(data as any);
    })();
    return () => { alive = false; };
  }, [open]);

  // Top up promo_video_url directly from the row when editing — the
  // list RPC doesn't surface it and admins have RLS read access.
  useEffect(() => {
    if (!open || !festival?.id) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('festivals')
        .select('promo_video_url, promo_starts_at, tickets_close_at')
        .eq('id', festival.id)
        .maybeSingle();
      if (alive && data) {
        const url = (data as any).promo_video_url || '';
        setPromoVideoUrl(url);
        originalPromoVideoUrlRef.current = url;
        const promoStart = isoDate((data as any).promo_starts_at);
        setPromoStartsAt(promoStart);
        originalPromoStartsAtRef.current = promoStart;
        setTicketsCloseAt(isoDate((data as any).tickets_close_at));
      }
    })();
    return () => { alive = false; };
  }, [open, festival?.id]);

  // Auto-derive slug from name (until the operator manually edits it)
  useEffect(() => {
    if (!slugTouched && !isEdit) {
      setSlug(slugify(name));
    }
  }, [name, slugTouched, isEdit]);

  // When the category changes (and we're CREATING), re-apply that
  // category's defaults so the operator gets a sensible preset.
  // We don't auto-stomp during edits because the operator may
  // have customised these values.
  const onCategoryChange = (next: Category) => {
    setCategory(next);
    if (!isEdit) {
      const def = CATEGORIES.find((c) => c.key === next)!;
      setThemeColor(def.defaultThemeColor);
      setEmoji(def.defaultEmoji);
      // If tiers haven't been touched (still equal an earlier preset),
      // swap to the new category's preset. Otherwise leave alone so
      // we don't blow away the operator's edits.
      const allPresets = Object.values(DEFAULT_TIER_PRESETS);
      const isUntouched = allPresets.some(
        (p) => JSON.stringify(p) === JSON.stringify(tiers),
      );
      if (isUntouched) setTiers(DEFAULT_TIER_PRESETS[next]);
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!name.trim()) errs.push('Name is required.');
    if (!slug.trim()) errs.push('Slug is required.');
    if (!startsAt) errs.push('Start date is required.');
    if (!endsAt) errs.push('End date is required.');
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
      errs.push('End date must be after the start date.');
    }
    if (promoStartsAt && startsAt && new Date(promoStartsAt) > new Date(startsAt)) {
      errs.push('"Start promoting on" must be on or before the start date.');
    }
    // A bare host is fine — we prepend https:// on save. Just guard
    // against obvious junk (needs a dot to look like a domain).
    if (ticketUrl && !/\./.test(ticketUrl)) {
      errs.push('Ticket URL doesn’t look like a valid web address.');
    }
    if (
      ticketsOnSaleAt && ticketsCloseAt &&
      new Date(ticketsCloseAt) <= new Date(ticketsOnSaleAt)
    ) {
      errs.push('"Tickets on sale until" must be after the on-sale time.');
    }
    return errs;
  }, [name, slug, startsAt, endsAt, promoStartsAt, ticketUrl, ticketsOnSaleAt, ticketsCloseAt]);

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_upsert_festival', {
        p_slug: slug,
        p_name: name,
        p_starts_at: dominicaLocalToIso(startsAt),
        p_ends_at: dominicaLocalToIso(endsAt),
        p_category: category,
        p_organizer_name: organizer || null,
        p_external_url: normalizeUrlMaybe(externalUrl),
        p_hero_image_url: heroImageUrl || null,
        p_theme_color: themeColor,
        p_emoji: emoji || null,
        p_description: description || null,
        p_venue_name: venueName || null,
        p_venue_lat: venueLat ? Number(venueLat) : null,
        p_venue_lng: venueLng ? Number(venueLng) : null,
        p_corridor_ids: corridorIds,
        p_sub_events: subEvents,
        p_sponsorship_tiers: tiers,
        p_is_recurring: isRecurring,
        p_recurrence_note: recurrenceNote || null,
        p_is_active: isActive,
        p_ticket_url: normalizeUrlMaybe(ticketUrl),
        p_ticket_partner_name: ticketPartnerName || null,
        p_ticket_price_from_xcd: ticketPriceFromXcd ? Number(ticketPriceFromXcd) : null,
        p_tickets_on_sale_at: ticketsOnSaleAt ? dominicaLocalToIso(ticketsOnSaleAt) : null,
        p_audience: audience,
      });
      if (error) throw error;
      const env = data as { ok?: boolean; error?: string; created?: boolean; id?: string };
      if (env?.ok === false) throw new Error(env.error || 'Save failed');

      // promo_video_url isn't a parameter on admin_upsert_festival, so we
      // persist it with a direct RLS-guarded write once we have the row id.
      if (env?.id) {
        // promo_video_url and promo_starts_at aren't parameters on
        // admin_upsert_festival, so we persist them with a direct
        // RLS-guarded write once we have the row id.
        const { error: vidErr } = await supabase
          .from('festivals')
          .update({
            promo_video_url: promoVideoUrl || null,
            promo_starts_at: promoStartsAt ? dominicaLocalToIso(promoStartsAt) : null,
            tickets_close_at: ticketsCloseAt ? dominicaLocalToIso(ticketsCloseAt) : null,
          })
          .eq('id', env.id);
        if (vidErr) throw vidErr;

        // The row now points at the new value, so the old persisted file
        // is safe to reclaim from storage (best-effort — never blocks save).
        const prev = originalPromoVideoUrlRef.current;
        if (prev && prev !== (promoVideoUrl || '')) {
          void deleteSponsorCreativeByUrl(prev);
        }
        originalPromoVideoUrlRef.current = promoVideoUrl || '';
        originalPromoStartsAtRef.current = promoStartsAt || '';
      }

      // Reclaim the previous hero image if it was replaced or cleared.
      // hero_image_url is saved by the RPC above; here we only clean up
      // the now-superseded file (best-effort — never blocks the save).
      {
        const prevHero = originalHeroImageUrlRef.current;
        if (prevHero && prevHero !== (heroImageUrl || '')) {
          void deleteSponsorCreativeByUrl(prevHero);
        }
        originalHeroImageUrlRef.current = heroImageUrl || '';
      }

      toast.success(env?.created ? `${name} added` : `${name} updated`);
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1117] border-gray-800 text-white !max-w-[min(900px,96vw)] max-h-[92vh] overflow-hidden p-0 sm:rounded-2xl">
        <div className="flex flex-col h-[88vh]">

          <DialogHeader className="shrink-0 px-6 py-4 border-b border-gray-800">
            <DialogTitle className="text-white text-lg">
              {isEdit ? `Edit ${festival?.name}` : 'New event'}
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-xs">
              Festival, concert, party, sports, market, civic, or private event.
              Slug is the natural key, so keep it stable across edits.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

            {/* ── 1. Identity ────────────────────────────────────────── */}
            <Section title="1. Identity">
              <Field label="Category">
                <Select
                  value={category}
                  onValueChange={(v) => onCategoryChange(v as Category)}
                >
                  <SelectTrigger className="bg-gray-900 border-gray-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    {CATEGORIES.map((c) => {
                      const Icon = c.icon;
                      return (
                        <SelectItem key={c.key} value={c.key} className="text-white">
                          <div className="flex items-center gap-2">
                            <Icon className="w-3.5 h-3.5" />
                            {c.label}
                            <span className="text-[11px] text-gray-500">
                              · {c.description}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Event name">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="World Creole Music Festival 2027"
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
                <Field label="Slug (URL identifier)">
                  <Input
                    value={slug}
                    onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
                    placeholder="wcmf-2027"
                    className="bg-gray-900 border-gray-800 text-white font-mono text-sm"
                    disabled={isEdit}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Starts at (Dominica time · AST)">
                  <Input
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
                <Field label="Ends at (Dominica time · AST)">
                  <Input
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-gray-500 -mt-1">
                All event times are entered and shown in Dominica time (AST,
                UTC-4). They're stored correctly even if you're scheduling from
                another timezone.
              </p>

              <Field label="Start promoting on (optional · Dominica time)">
                <div className="flex items-center gap-2">
                  <Input
                    type="datetime-local"
                    value={promoStartsAt}
                    onChange={(e) => setPromoStartsAt(e.target.value)}
                    className="bg-gray-900 border-gray-800 text-white flex-1"
                  />
                  {promoStartsAt && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPromoStartsAt('')}
                      className="shrink-0 border-gray-700 text-gray-300 hover:text-white"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </Field>
              <p className="text-[11px] text-gray-500 -mt-1">
                Leave blank to show the event only during its dates. Set an
                earlier date to start hyping it in the app ahead of time. It
                appears as <span className="text-emerald-300">"Upcoming"</span>{' '}
                with a live countdown and the Get&nbsp;Tickets button, then flips
                to <span className="text-emerald-300">"Live"</span> automatically
                on the day. Great for driving pre-sales on parties and one-night
                events.{' '}
                <span className="text-gray-400">
                  Press <span className="text-white font-medium">Clear</span> to
                  turn off early promotion. The event will then simply go live on
                  its start date.
                </span>
              </p>

              <Field label="Description (sales copy + mobile detail sheet)">
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Three-night flagship Creole music festival drawing 12,000+ attendees..."
                  rows={3}
                  className="bg-gray-900 border-gray-800 text-white resize-none"
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Organizer">
                  <Input
                    value={organizer}
                    onChange={(e) => setOrganizer(e.target.value)}
                    placeholder="Discover Dominica Authority"
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
                <Field label="Official website">
                  <Input
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="https://dominicafestivals.com/wcmf"
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
              </div>
            </Section>

            {/* ── 2. Tickets ────────────────────────────────────────── */}
            <Section
              title="2. Tickets (where people buy)"
              description="Paste the link where riders buy tickets: KairiTickets, Eventbrite, or the promoter's own page. This powers the Get Tickets button in the app. We don't process payments; we drive traffic and track the click-through so you can quote conversions to sponsors."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Ticket URL">
                  <Input
                    value={ticketUrl}
                    onChange={(e) => setTicketUrl(e.target.value)}
                    placeholder="https://kairitickets.com/wcmf-2027"
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
                <Field label="Ticket partner">
                  <Input
                    value={ticketPartnerName}
                    onChange={(e) => setTicketPartnerName(e.target.value)}
                    placeholder="KairiTickets"
                    className="bg-gray-900 border-gray-800 text-white"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Starting price (EC$)">
                  <Input
                    type="number"
                    min={0}
                    value={ticketPriceFromXcd}
                    onChange={(e) => setTicketPriceFromXcd(e.target.value)}
                    placeholder="50"
                    className="bg-gray-900 border-gray-800 text-white font-mono"
                  />
                </Field>
                <Field label="On sale at (optional)">
                  <div className="flex items-center gap-2">
                    <Input
                      type="datetime-local"
                      value={ticketsOnSaleAt}
                      onChange={(e) => setTicketsOnSaleAt(e.target.value)}
                      className="bg-gray-900 border-gray-800 text-white flex-1"
                    />
                    {ticketsOnSaleAt && (
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setTicketsOnSaleAt('')}
                        className="shrink-0 border-gray-700 text-gray-300 hover:text-white"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Tickets on sale until (optional)">
                  <div className="flex items-center gap-2">
                    <Input
                      type="datetime-local"
                      value={ticketsCloseAt}
                      onChange={(e) => setTicketsCloseAt(e.target.value)}
                      className="bg-gray-900 border-gray-800 text-white flex-1"
                    />
                    {ticketsCloseAt && (
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setTicketsCloseAt('')}
                        className="shrink-0 border-gray-700 text-gray-300 hover:text-white"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </Field>
              </div>
              <p className="text-[11px] text-gray-500 -mt-1">
                Leave "on sale until" blank to keep the Get&nbsp;Tickets button up
                for the whole event (best for multi-day festivals or door sales).
                Set it (e.g. door time for a one-night party) and the app
                automatically swaps the button for{' '}
                <span className="text-gray-300">"Ticket sales closed"</span> after
                that moment, so no one taps into a dead checkout.
              </p>
              {ticketUrl && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-xs text-emerald-200 flex items-start gap-2">
                  <Ticket className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Live preview: passengers will see a "Get tickets
                    {ticketPartnerName && ` · ${ticketPartnerName}`}
                    {ticketPriceFromXcd && ` · from EC$${ticketPriceFromXcd}`}"
                    button on the festival detail sheet during this event's window.
                  </div>
                </div>
              )}
            </Section>

            {/* ── 3. Branding ───────────────────────────────────────── */}
            <Section title="3. Branding">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Theme color (hex)">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={themeColor}
                      onChange={(e) => setThemeColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-800 bg-transparent cursor-pointer"
                    />
                    <Input
                      value={themeColor}
                      onChange={(e) => setThemeColor(e.target.value)}
                      className="bg-gray-900 border-gray-800 text-white font-mono text-sm flex-1"
                    />
                  </div>
                </Field>
                <Field label="Emoji">
                  <Input
                    value={emoji}
                    onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
                    placeholder="🎶"
                    className="bg-gray-900 border-gray-800 text-white text-2xl text-center"
                  />
                </Field>
                <Field label="Hero image (upload or paste a URL)">
                  <ImageUploadInput
                    value={heroImageUrl}
                    onChange={setHeroImageUrl}
                    folder="festivals"
                    placeholder="https://…  or upload →"
                  />
                </Field>
              </div>
              {heroImageUrl && (
                <div
                  className="h-24 rounded-lg border border-gray-800 overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${themeColor}cc, transparent), url(${heroImageUrl}) center/cover`,
                  }}
                >
                  <div className="h-full flex items-end p-3 text-white text-sm font-bold drop-shadow">
                    {emoji} {name || 'Event preview'}
                  </div>
                </div>
              )}

              <Field label="Promo video (optional · MP4 or MOV)">
                <VideoUploadInput
                  value={promoVideoUrl}
                  onChange={setPromoVideoUrl}
                  folder="festival-promos"
                  placeholder="https://…  or upload a promo video →"
                  maxMb={100}
                  maxSeconds={180}
                />
              </Field>
              <p className="text-[11px] text-gray-500 -mt-1">
                Great for carnival / festival teaser reels. Passengers see a
                "Watch promo" card on the event's detail sheet and tap to play
                it (with sound). It never auto-plays, so it won't burn their
                data. Up to 3 minutes and 100 MB, but 60–90 seconds keeps
                people watching. The hero image above is used as the
                tap-to-play thumbnail.
              </p>
            </Section>

            {/* ── 4. Venue ──────────────────────────────────────────── */}
            <Section
              title="4. Venue"
              description="Pick the exact location where the in-app glow will appear. Click the map to drop the pin or drag it once placed. The lat/lng fields below stay editable for operators with precise coordinates."
            >
              <Field label="Venue name">
                <Input
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  placeholder="Windsor Park Stadium"
                  className="bg-gray-900 border-gray-800 text-white"
                />
              </Field>
              {/*
                Live map picker — same Leaflet + OSM stack the campaign
                wizard uses, with the pin styled to match the actual
                mobile FestivalGlowMarker (theme color badge + emoji +
                halo at the real 360 m radius) for an honest WYSIWYG
                preview. Drag the pin OR click anywhere on the map to
                move the glow. The lat/lng fields below stay editable.
              */}
              <FestivalVenuePinPicker
                lat={venueLat ? Number(venueLat) : null}
                lng={venueLng ? Number(venueLng) : null}
                themeColor={themeColor}
                emoji={emoji}
                label={venueName || name}
                onChange={(nextLat, nextLng) => {
                  setVenueLat(String(nextLat));
                  setVenueLng(String(nextLng));
                }}
              />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Latitude">
                  <Input
                    value={venueLat}
                    onChange={(e) => setVenueLat(e.target.value)}
                    placeholder="15.30421"
                    className="bg-gray-900 border-gray-800 text-white font-mono text-sm"
                  />
                </Field>
                <Field label="Longitude">
                  <Input
                    value={venueLng}
                    onChange={(e) => setVenueLng(e.target.value)}
                    placeholder="-61.38704"
                    className="bg-gray-900 border-gray-800 text-white font-mono text-sm"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-gray-500">
                Tip: right-click the venue on Google Maps and copy the lat/lng pair.
              </p>
            </Section>

            {/* ── 5. Reach ──────────────────────────────────────────── */}
            <Section title="5. Reach (corridors that surge for this event)">
              {allCorridors.length === 0 ? (
                <p className="text-xs text-gray-500">No corridors loaded.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {allCorridors.map((c) => {
                    const selected = corridorIds.includes(c.id);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => {
                          setCorridorIds(
                            selected
                              ? corridorIds.filter((id) => id !== c.id)
                              : [...corridorIds, c.id],
                          );
                        }}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                          selected
                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                            : 'bg-gray-900 text-gray-400 border-gray-800 hover:border-gray-600'
                        }`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* ── 5b. Audience targeting ────────────────────────────── */}
            <Section
              title="5b. Audience"
              description="Same rule as Sponsored Map Pin campaigns. Choose who sees this event's pocket-of-light glow on the in-app maps. Passenger map = passenger home; Driver Discover tab = driver off-shift surface."
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {([
                  {
                    value: 'all' as const,
                    label: 'Everyone',
                    hint: 'Passenger + driver',
                  },
                  {
                    value: 'passenger' as const,
                    label: 'Passenger only',
                    hint: 'Hidden from driver app',
                  },
                  {
                    value: 'driver' as const,
                    label: 'Driver only',
                    hint: 'Driver Discover tab',
                  },
                  {
                    value: 'tourist' as const,
                    label: 'Tourist only',
                    hint: 'Tourist-tagged riders',
                  },
                ]).map((opt) => {
                  const selected = audience === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setAudience(opt.value)}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        selected
                          ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                          : 'bg-gray-900 text-gray-300 border-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <div className="text-sm font-semibold">{opt.label}</div>
                      <div className="text-[11px] mt-0.5 opacity-70">{opt.hint}</div>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* ── 6. Sub-events ─────────────────────────────────────── */}
            <Section
              title="6. Sub-events (optional)"
              description="Use for festivals with multiple distinct nights or sub-shows (Calypso Monarch, J'Ouvert, etc.). Sponsors can buy individual sub-events as 'stage' tier slots."
              actions={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSubEvents([
                    ...subEvents,
                    {
                      slug: `sub-event-${subEvents.length + 1}`,
                      name: '',
                      starts_at: startsAt ? (dominicaLocalToIso(startsAt) ?? '') : '',
                      ends_at: endsAt ? (dominicaLocalToIso(endsAt) ?? '') : '',
                    },
                  ])}
                  className="text-xs border-gray-700"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add sub-event
                </Button>
              }
            >
              {subEvents.length === 0 ? (
                <p className="text-xs text-gray-500">No sub-events. Single-show events leave this empty.</p>
              ) : (
                <div className="space-y-2">
                  {subEvents.map((se, i) => (
                    <div key={i} className="flex items-end gap-2 bg-gray-900/40 rounded-lg p-2 border border-gray-800">
                      <Field label="Slug" className="flex-[1.2]">
                        <Input
                          value={se.slug}
                          onChange={(e) => {
                            const next = [...subEvents];
                            next[i] = { ...se, slug: slugify(e.target.value) };
                            setSubEvents(next);
                          }}
                          className="bg-gray-900 border-gray-800 text-white font-mono text-xs h-8"
                        />
                      </Field>
                      <Field label="Name" className="flex-[2]">
                        <Input
                          value={se.name}
                          onChange={(e) => {
                            const next = [...subEvents];
                            next[i] = { ...se, name: e.target.value };
                            setSubEvents(next);
                          }}
                          className="bg-gray-900 border-gray-800 text-white text-xs h-8"
                        />
                      </Field>
                      <Field label="Starts" className="flex-[1.5]">
                        <Input
                          type="datetime-local"
                          value={isoDate(se.starts_at)}
                          onChange={(e) => {
                            const next = [...subEvents];
                            next[i] = { ...se, starts_at: dominicaLocalToIso(e.target.value) ?? '' };
                            setSubEvents(next);
                          }}
                          className="bg-gray-900 border-gray-800 text-white text-xs h-8"
                        />
                      </Field>
                      <Field label="Ends" className="flex-[1.5]">
                        <Input
                          type="datetime-local"
                          value={isoDate(se.ends_at)}
                          onChange={(e) => {
                            const next = [...subEvents];
                            next[i] = { ...se, ends_at: dominicaLocalToIso(e.target.value) ?? '' };
                            setSubEvents(next);
                          }}
                          className="bg-gray-900 border-gray-800 text-white text-xs h-8"
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={() => setSubEvents(subEvents.filter((_, j) => j !== i))}
                        className="p-2 text-rose-400 hover:bg-rose-500/10 rounded h-8"
                        aria-label="Remove sub-event"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* ── 7. Sponsorship tiers ──────────────────────────────── */}
            <Section
              title="7. Sponsorship tiers"
              description="Each tier is a separate revenue line. Sponsors buy slots in any combination."
              actions={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setTiers([
                    ...tiers,
                    {
                      tier: 'venue_partner' as FestivalTierKey,
                      label: 'Venue partner',
                      slot_count: 1,
                      rate_xcd_min: 500,
                      rate_xcd_max: 1000,
                      perks: [],
                    },
                  ])}
                  className="text-xs border-gray-700"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add tier
                </Button>
              }
            >
              {tiers.map((t, i) => (
                <div key={i} className="bg-gray-900/40 rounded-lg p-3 border border-gray-800 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <Field label="Tier" className="md:col-span-3">
                      <Select
                        value={t.tier}
                        onValueChange={(v) => {
                          const next = [...tiers];
                          next[i] = { ...t, tier: v as FestivalTierKey };
                          setTiers(next);
                        }}
                      >
                        <SelectTrigger className="bg-gray-900 border-gray-800 text-white h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-800 text-white">
                          <SelectItem value="title">Title</SelectItem>
                          <SelectItem value="stage">Stage</SelectItem>
                          <SelectItem value="corridor">Corridor</SelectItem>
                          <SelectItem value="venue_partner">Venue partner</SelectItem>
                          <SelectItem value="after_party">After-party</SelectItem>
                          <SelectItem value="sub_event">Sub-event</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Label" className="md:col-span-3">
                      <Input
                        value={t.label}
                        onChange={(e) => {
                          const next = [...tiers];
                          next[i] = { ...t, label: e.target.value };
                          setTiers(next);
                        }}
                        className="bg-gray-900 border-gray-800 text-white h-8 text-xs"
                      />
                    </Field>
                    <Field label="Slots" className="md:col-span-1">
                      <Input
                        type="number"
                        min={1}
                        value={t.slot_count}
                        onChange={(e) => {
                          const next = [...tiers];
                          next[i] = { ...t, slot_count: Math.max(1, Number(e.target.value)) };
                          setTiers(next);
                        }}
                        className="bg-gray-900 border-gray-800 text-white h-8 text-xs font-mono"
                      />
                    </Field>
                    <Field label="Min XCD" className="md:col-span-2">
                      <Input
                        type="number"
                        min={0}
                        value={t.rate_xcd_min}
                        onChange={(e) => {
                          const next = [...tiers];
                          next[i] = { ...t, rate_xcd_min: Number(e.target.value) };
                          setTiers(next);
                        }}
                        className="bg-gray-900 border-gray-800 text-white h-8 text-xs font-mono"
                      />
                    </Field>
                    <Field label="Max XCD" className="md:col-span-2">
                      <Input
                        type="number"
                        min={0}
                        value={t.rate_xcd_max}
                        onChange={(e) => {
                          const next = [...tiers];
                          next[i] = { ...t, rate_xcd_max: Number(e.target.value) };
                          setTiers(next);
                        }}
                        className="bg-gray-900 border-gray-800 text-white h-8 text-xs font-mono"
                      />
                    </Field>
                    <button
                      type="button"
                      onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                      className="md:col-span-1 p-2 text-rose-400 hover:bg-rose-500/10 rounded h-8 self-end"
                      aria-label="Remove tier"
                    >
                      <Trash2 className="w-3.5 h-3.5 mx-auto" />
                    </button>
                  </div>
                  <Field label="Perks (one per line)">
                    <Textarea
                      value={(t.perks || []).join('\n')}
                      onChange={(e) => {
                        const next = [...tiers];
                        next[i] = {
                          ...t,
                          perks: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                        };
                        setTiers(next);
                      }}
                      rows={2}
                      className="bg-gray-900 border-gray-800 text-white text-xs resize-none"
                      placeholder="Full-screen splash when the app opens&#10;Logo on every surface&#10;..."
                    />
                  </Field>
                </div>
              ))}
            </Section>

            {/* ── 8. Recurrence + active toggle ─────────────────────── */}
            <Section title="8. Operations">
              <div className="flex items-center justify-between gap-4 bg-gray-900/40 border border-gray-800 rounded-lg p-3">
                <div>
                  <Label className="text-white text-sm">Active</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Inactive events are hidden from the dashboard and mobile app.
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>
              <div className="flex items-center justify-between gap-4 bg-gray-900/40 border border-gray-800 rounded-lg p-3">
                <div>
                  <Label className="text-white text-sm">Recurring annually</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Used by the regenerate-next-year tool. Leave on for festivals; off for one-offs.
                  </p>
                </div>
                <Switch checked={isRecurring} onCheckedChange={setIsRecurring} />
              </div>
              <Field label="Recurrence note (for ops team)">
                <Input
                  value={recurrenceNote}
                  onChange={(e) => setRecurrenceNote(e.target.value)}
                  placeholder="Annual end-of-October flagship. Refresh dates each January."
                  className="bg-gray-900 border-gray-800 text-white"
                />
              </Field>
            </Section>

            {/* Validation summary */}
            {errors.length > 0 && (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-xs text-rose-200">
                <div className="font-bold mb-1">Fix these before saving:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-gray-800 bg-[#0d1117]">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || errors.length > 0}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />}
              {isEdit ? 'Save changes' : 'Create event'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────────────

function Section({
  title, description, actions, children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-white">{title}</h3>
          {description && (
            <p className="text-[11px] text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({
  label, children, className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
